import { Component, ViewEncapsulation, OnInit, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UserService } from 'src/app/services/user/user.service';
import { UserModalService } from 'src/app/services/user-modal/user-modal.service';
import { Client, PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { USER_TYPES } from 'src/app/config/system.config';
import { ClientService } from 'src/app/services/user/client.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
declare var KTMenu: any;
declare var bootstrap: any;



// Define filter interface
interface ArApFilters {
  salesPerson?: any;
  customer?: any;
  report_date?: string;
}

@Component({
  selector: 'app-ar-ap',
  templateUrl: './ar-ap.component.html',
  styleUrls: ['./ar-ap.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ArApComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() clientUpdated = new EventEmitter<void>();

  // Data properties
  clients: Client[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  totalReceivable: number = 0;
  totalPayable: number = 0;

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  isExportingExcel: boolean = false;

  // Currency filter
  selectedCurrency: string = 'usd';

  // Filter properties
  showFilters: boolean = false;
  filters: ArApFilters = {};

  // Customer search properties
  customerSearchTerm: string = '';
  selectedCustomer: any = null;
  customerSearchResults: any[] = [];
  showCustomerDropdown: boolean = false;
  isCustomerSearching: boolean = false;
  private customerSearchSubject = new Subject<string>();

  // Sales person search properties
  salesPersonSearchTerm: string = '';
  selectedSalesPerson: any = null;
  salesPersonSearchResults: any[] = [];
  showSalesPersonDropdown: boolean = false;
  isSalesPersonSearching: boolean = false;
  private salesPersonSearchSubject = new Subject<string>();

  // Search subject for debouncing
  private searchSubject = new Subject<string>();

  constructor(
    private userService: UserService,
    private _clientService: ClientService,
    private userModalService: UserModalService,
    private generalService: GeneralService,
    private _router: Router

  ) {
    super();
    this.setupSearchDebounce();
    this.setupCustomerSearchDebounce();
    this.setupSalesPersonSearchDebounce();
  }

  ngOnInit(): void {
    this.loadClients();
  }

  ngAfterViewInit(): void {
    // Initialize KTMenu instances for dropdown menus
    this.initializeMenus();
  }

  setupSearchDebounce(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.loadClients(1, searchTerm);
      })
    );
  }

  setupCustomerSearchDebounce(): void {
    this.subscriptions.push(
      this.customerSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchCustomers(searchTerm);
      })
    );
  }

  setupSalesPersonSearchDebounce(): void {
    this.subscriptions.push(
      this.salesPersonSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchSalesPersons(searchTerm);
      })
    );
  }

  loadClients(page: number = this.currentPage, search: string = this.searchTerm, filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    // Use provided filters or build default filters
    const apiFilters = Object.keys(filters).length > 0 ? filters : {
      role_type: USER_TYPES.CLIENT,
      system_user: "false",
      is_report: true,
      currency_type: this.selectedCurrency
    };

    this.subscriptions.push(
      this._clientService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<Client>) => {
          if (response.status && response.data) {
            this.clients = response.data.data || [];
            this.paginationInfo = response.data.pagination;

            if (Object.keys(response.data.pagination).includes('account_payable')) {
              this.totalReceivable = response.data.pagination.account_receivable;
              this.totalPayable = response.data.pagination.account_payable;
            }
            this.currentPage = response.data.pagination.currentPage;
            this.refreshMenus();
          } else {
            this.clients = [];
            this.resetPagination();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading clients:', error);
          this.clients = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(this.searchTerm);
  }

  onPageChange(page: number): void {
    this.loadClients(page);
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filters).some(value => value !== '' && value !== null && value !== undefined);
  }

  getActiveFiltersCount(): number {
    return Object.values(this.filters).filter(value => value !== '' && value !== null && value !== undefined).length;
  }

  clearFilters(): void {
    this.filters = {};
    // Clear customer search
    this.clearCustomerSearch();
    // Clear sales person search
    this.clearSalesPersonSearch();
    this.applyFilters();
  }

  applyFilters(): void {
    // Build filters for API call
    const apiFilters: any = { role_type: USER_TYPES.CLIENT, system_user: "false", is_report: true };

    // Add currency filter
    if (this.selectedCurrency) {
      apiFilters.currency_type = this.selectedCurrency;
    }

    // Add customer filter
    if (this.filters.customer) {
      apiFilters.customer_id = this.filters.customer._id;
    }

    // Add sales person filter
    if (this.filters.salesPerson) {
      apiFilters.sales_person_id = this.filters.salesPerson._id;
    }

    // Add date filter
    if (this.filters.report_date) {
      apiFilters.report_date = this.filters.report_date;
    }

    this.loadClients(1, this.searchTerm, apiFilters);
  }



  // Initialize KTMenu instances
  initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Utility methods for template
  getAvatarDisplay(client: Client): { type: 'image' | 'initial', value: string, bgClass?: string } {
    if (client.avatar && client.avatar.trim() !== '') {
      return { type: 'image', value: client.avatar };
    }

    const name = client.name || client.email || 'C';
    const initial = name.charAt(0).toUpperCase();
    const bgClasses = ['bg-light-primary text-primary', 'bg-light-success text-success',
      'bg-light-info text-info', 'bg-light-warning text-warning',
      'bg-light-danger text-danger'];
    const bgClass = bgClasses[initial.charCodeAt(0) % bgClasses.length];

    return { type: 'initial', value: initial, bgClass };
  }

  // Track by function for ngFor performance
  trackByClientId(index: number, client: Client): string {
    return client._id || client.doc_id || index.toString();
  }

  // Method to handle add client
  addClient(): void {
    console.log('Add client');

    // Clear any selected user from service
    this.userModalService.clearSelectedUser();

    // Dispatch custom event for add client
    const addEvent = new CustomEvent('addClient', {
      bubbles: true
    });
    console.log('Dispatching addClient event:', addEvent);
    document.dispatchEvent(addEvent);

    // Small delay to ensure data is cleared
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_modal_edit_client');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Method to handle edit client
  editClient(client: Client): void {
    console.log('Edit client:', client);

    // Use the service to set the selected user/client
    this.userModalService.setSelectedUser(client);

    // Also dispatch custom event as backup
    const editEvent = new CustomEvent('editClient', {
      detail: client,
      bubbles: true
    });
    console.log('Dispatching editClient event:', editEvent);
    document.dispatchEvent(editEvent);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_modal_edit_client');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Handle client updated event
  onClientUpdated(): void {
    this.loadClients(); // Refresh the clients list
    this.clientUpdated.emit(); // Emit event to parent component
  }

  // Currency filter methods
  onCurrencyChange(currency: string): void {
    this.selectedCurrency = currency;
    this.loadClients(1); // Reset to first page when filtering
  }

  // Customer search methods
  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  searchCustomers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      return;
    }

    this.isCustomerSearching = true;
    this.subscriptions.push(
      this._clientService.GetData('1', searchTerm, {
        role_type: USER_TYPES.CLIENT
      }).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    this.customerSearchTerm = this.getCustomerDisplayName(customer);
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = customer;
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = null;
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}`;
    }
    return customer.name || customer.email || 'Unknown Customer';
  }

  // Sales person search methods
  onSalesPersonSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.salesPersonSearchTerm = target.value;
    this.selectedSalesPerson = null;
    this.salesPersonSearchSubject.next(this.salesPersonSearchTerm);
    this.showSalesPersonDropdown = true;
  }

  onSalesPersonInputFocus(): void {
    if (this.salesPersonSearchResults.length > 0) {
      this.showSalesPersonDropdown = true;
    }
  }

  onSalesPersonInputBlur(): void {
    setTimeout(() => {
      this.showSalesPersonDropdown = false;
    }, 200);
  }

  searchSalesPersons(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.salesPersonSearchResults = [];
      this.showSalesPersonDropdown = false;
      return;
    }

    this.isSalesPersonSearching = true;
    this.subscriptions.push(
      this.userService.GetData('1', searchTerm, { is_primary_agent: true }).subscribe({
        next: (response: any) => {
          this.isSalesPersonSearching = false;
          if (response.status && response.data && response.data.data) {
            this.salesPersonSearchResults = response.data.data;
            this.showSalesPersonDropdown = this.salesPersonSearchResults.length > 0;
          } else {
            this.salesPersonSearchResults = [];
            this.showSalesPersonDropdown = false;
          }
        },
        error: (error: any) => {
          this.isSalesPersonSearching = false;
          console.error('Error searching sales persons:', error);
          this.salesPersonSearchResults = [];
          this.showSalesPersonDropdown = false;
        }
      })
    );
  }

  selectSalesPerson(salesPerson: any): void {
    this.selectedSalesPerson = salesPerson;
    this.salesPersonSearchTerm = this.getSalesPersonDisplayName(salesPerson);
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
    this.filters.salesPerson = salesPerson;
  }

  clearSalesPersonSearch(): void {
    this.salesPersonSearchTerm = '';
    this.selectedSalesPerson = null;
    this.showSalesPersonDropdown = false;
    this.salesPersonSearchResults = [];
    this.filters.salesPerson = null;
  }

  getSalesPersonDisplayName(salesPerson: any): string {
    if (salesPerson.title && salesPerson.name) {
      return `${salesPerson.title} ${salesPerson.name}`;
    }
    return salesPerson.name || salesPerson.email || 'Unknown Sales Person';
  }

  // Export Excel method
  exportExcel(): void {
    // Set loading state
    this.isExportingExcel = true;

    // Prepare filters object
    const filtersObj: any = this.hasActiveFilters() ? { ...this.filters } : {};

    // Add type parameter
    filtersObj.type = 'report_ar_ap';
    filtersObj.excel_name = 'ar ap report';
    filtersObj.system_user = "false";

    // Add currency filter
    if (this.selectedCurrency) {
      filtersObj.currency_type = this.selectedCurrency;
    }

    // Add customer filter
    if (this.filters.customer) {
      filtersObj.customer_id = this.filters.customer._id;
    }

    // Add sales person filter
    if (this.filters.salesPerson) {
      filtersObj.sales_person_id = this.filters.salesPerson._id;
    }

    // Add date filter
    if (this.filters.report_date) {
      filtersObj.report_date = this.filters.report_date;
    }

    // Add search term if present
    if (this.searchTerm) {
      filtersObj.search = this.searchTerm;
    }

    console.log('Exporting Excel with filters:', filtersObj);

    // Call the generateExcelPost method
    this.subscriptions.push(
      this.generalService.generateExcelPost(filtersObj).subscribe({
        next: (response: any) => {
          this.isExportingExcel = false;
          console.log('Excel export response:', response);

          if (response.status && response.data && response.data.url) {
            // Open the Excel file URL in a new tab
            window.open(response.data.url, '_blank');
          } else {
            // Show error message if no URL is returned
            Swal.fire({
              text: 'Failed to generate Excel file. No URL received.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          this.isExportingExcel = false;
          console.error('Error exporting Excel:', error);
          Swal.fire({
            text: 'An error occurred while exporting Excel file.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }

  viewDetailClient(client: any): void {
    if (client && client._id) {
      const url = this._router.serializeUrl(
        this._router.createUrlTree(['sales/client-manager', client._id])
      );

      window.open(url, '_blank');
    }
  }
}
